// src/components/PIIAnonymizerPage/PIIAnonymizationOptionsDialog/PIIAnonymizationOptionsDialog.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';

import { getAnonymizationProfiles } from '../../../../services/anonymizationProfilesService';
import { PROCESS_TYPES } from '../../../../constants/processTypes';

import OptionsTab from './OptionsTab';
import ProfilesTab from '../../../shared/ProfilesTab/ProfilesTab';
import DefaultTemplatesTab from './DefaultTemplatesTab';

const PIIAnonymizationOptionsDialog = ({
  open,
  options,
  onChangeOptions,
  onClose,
  onConfirm,
  loading,
  pseudoAnonymization = false,
}) => {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState('');
  const [tab, setTab] = useState('templates');

  const processType = useMemo(
    () =>
      pseudoAnonymization
        ? PROCESS_TYPES.PSEUDONYMIZATION
        : PROCESS_TYPES.ANONYMIZATION,
    [pseudoAnonymization]
  );

  useEffect(() => {
    if (!open) return;

    const loadProfiles = async () => {
      try {
        setProfilesLoading(true);
        setProfilesError('');
        const data = await getAnonymizationProfiles(processType);
        setProfiles(data || []);
      } catch (err) {
        console.error(err);
        setProfilesError(
          pseudoAnonymization
            ? t(
                'admin.pii.options.pseudoProfilesLoadError',
                'Nepodarilo sa načítať profily pseudonymizácie.'
              )
            : t(
                'admin.pii.options.profilesLoadError',
                'Nepodarilo sa načítať anonymizačné profily.'
              )
        );
      } finally {
        setProfilesLoading(false);
      }
    };

    loadProfiles();
  }, [open, t, processType, pseudoAnonymization]);

  // default tab when dialog opens
  useEffect(() => {
    if (!open) return;
    setTab('templates');
  }, [open]);

  const handleTabChange = (_event, value) => {
    setTab(value);

    if (value === 'options') {
      // switching to custom options -> clear both selectors
      if (options.profileId || options.defaultTemplateId) {
        onChangeOptions({ ...options, profileId: null, defaultTemplateId: null });
      }
    } else if (value === 'profiles') {
      // switching to profiles -> clear template
      if (options.defaultTemplateId) {
        onChangeOptions({ ...options, defaultTemplateId: null });
      }
    } else if (value === 'templates') {
      // switching to templates -> clear db profile
      if (options.profileId) {
        onChangeOptions({ ...options, profileId: null });
      }
    }
  };

  const handleToggle = (key) => (event) => {
    onChangeOptions({ ...options, [key]: event.target.checked });
  };

  const handleFieldChange = (key) => (value) => {
    onChangeOptions({ ...options, [key]: value });
  };

  const handleSelectProfile = (profileId) => {
    onChangeOptions({ ...options, profileId, defaultTemplateId: null });
  };

  const titleText = pseudoAnonymization
    ? t('admin.pii.options.pseudoTitle', 'Možnosti pseudonymizácie')
    : t('admin.pii.options.title', 'Možnosti anonymizácie');

  const subtitleText = t(
    'admin.pii.options.subtitle',
    'Vyberte, čo sa má počas spracovania aplikovať.'
  );

  const confirmText = pseudoAnonymization
    ? t('admin.pii.startPseudonymization', 'Spustiť pseudonymizáciu')
    : t('admin.pii.startAnonymization', 'Spustiť anonymizáciu');

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">{titleText}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitleText}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" size="small">
            <Tab value="templates" label={t('admin.pii.options.tab.templates', 'Predvolené')} />
            <Tab value="profiles" label={t('admin.pii.options.tab.profiles', 'Profil')} />
            <Tab value="options" label={t('admin.pii.options.tab.custom', 'Vlastné nastavenia')} />
          </Tabs>
        </Box>

        {tab === 'templates' && (
          <DefaultTemplatesTab
            selectedTemplateId={options.defaultTemplateId || null}
            onSelectTemplate={(id) =>
              onChangeOptions({ ...options, defaultTemplateId: id, profileId: null })
            }
          />
        )}

        {tab === 'profiles' && (
          <ProfilesTab
            profiles={profiles}
            loading={profilesLoading}
            error={profilesError}
            selectedProfileId={options.profileId || null}
            onSelectProfile={handleSelectProfile}
          />
        )}

        {tab === 'options' && (
          <OptionsTab
            options={options}
            onToggle={handleToggle}
            onChangeField={handleFieldChange}
            loading={loading}
            pseudoAnonymization={pseudoAnonymization}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Zrušiť')}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={<PlayArrowIcon />}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PIIAnonymizationOptionsDialog;
